import { ToastrService } from 'ngx-toastr';
import pluralize from 'pluralize';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListResponse, ISnListQueryParameters, ISnFeedQueryParameters } from '@lib/api';
import { SnOrganization, SnShare } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnSharesAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getShareList(params: ISnListQueryParameters): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get('/api/shares/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public getOrganizationsColor(id: string): Observable<any> {
    return this._httpClient.get(`/api/shares/settings/${id}/`);
  }

  public patchOrganizationColor(id: string, color: string): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/shares/settings/${id}/`, {
        color,
      })
      .pipe(map((response: Object) => new SnShare(response)));
  }

  public feedShares(params: ISnFeedQueryParameters): Observable<SnShare[]> {
    return this._httpClient
      .get('/api/shares/feed/', { params })
      .pipe(map((response: any) => response.map((share) => new SnShare(share))));
  }

  public getShare(id: string): Observable<SnShare> {
    return this._httpClient
      .get(`/api/shares/${id}/`)
      .pipe(map((response: Object) => new SnShare(response)));
  }

  public editShare(id: string, data: Object): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/shares/${id}/`, data)
      .pipe(map((response: Object) => new SnShare(response)));
  }

  public deleteShare(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('shares.confirmDeleteShare'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/shares/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('shares.shareDeleted')),
          error: () => this._toastrService.error(t('shares.couldNotDeleteShare')),
        }),
      );
  }

  public bulkAccept(ids: string[]): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('shares.confirmAcceptShares', { count: ids.length, shares: pluralize(t('common.share'), ids.length) }),
        confirmButton: {
          text: t('shares.accept'),
          color: 'accent',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.post('/api/shares/bulk/accept/', {
            objects: ids,
          });
        }),
        tap({
          next: () =>
            this._toastrService.success(
              t('shares.sharesAccepted', { count: ids.length, shares: pluralize(t('common.share'), ids.length) })
            ),
          error: () => this._toastrService.error(t('shares.couldNotAcceptShares')),
        }),
      );
  }

  public bulkDecline(ids: string[]): Observable<Object> {
    const count = ids.length;

    return this._dialogService
      .confirm({
        message: t('shares.confirmDeclineShares', { count, shares: pluralize(t('common.share'), count) }),
        confirmButton: {
          text: t('shares.decline'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.post('/api/shares/bulk/decline/', { objects: ids });
        }),
        tap({
          next: () =>
            this._toastrService.success(t('shares.sharesDeclined', { count, shares: pluralize(t('common.share'), count) })),
          error: () =>
            this._toastrService.error(t('shares.couldNotDeclineShares')),
        }),
      );
  }

  public bulkDelete(ids: string[]): Observable<Object> {
    const count = ids.length;

    return this._dialogService
      .confirm({
        message: t('shares.confirmDeleteShares', { count, shares: pluralize(t('common.share'), count) }),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.post('/api/shares/bulk/delete/', { objects: ids });
        }),
        tap({
          next: () => this._toastrService.success(t('shares.sharesDeleted', { count, shares: pluralize(t('common.share'), count) })),
          error: () => this._toastrService.error(t('shares.couldNotDeleteShares')),
        }),
      );
  }

  public getSharesOrgList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnOrganization>> {
    return this._httpClient.get('/api/shares/orglist/', { params }).pipe(
      map((response: any) => ({
        totalPages: response.totalPages || 1,
        count: response?.length || 0,
        links: response.links || {},
        results: response?.results?.map((organization) => new SnOrganization(organization)) || [],
      })),
    );
  }
}
